/**
 * Основание экрана: чем измерено то, что на нём показано.
 *
 * Закон седьмой дизайн-системы: рядом с числом всегда видно, что измерено, по
 * скольким движкам, за какой период и насколько полно. Правило **экранное, а не
 * поштучное** (data-viz.md §1) — строка стоит один раз в шапке, а не подписью к
 * каждому графику, — но существовать она обязана. Экран с одной датой вместо
 * основания показывает историческую точку с незакрытой сеткой как полную:
 * предупреждение о неполноте живёт только у активного прогона, а прогон давно
 * закончился.
 *
 * Реализация одна на «Обзор» и «Движки»: две копии этой фразы разъехались бы, и
 * один и тот же замер получил бы на соседних экранах разное основание. У
 * «Динамики» основание своё и про другое — там период и полнота по СУТКАМ, а не
 * одна точка.
 *
 * Строки — в каталоге (`basis.*`, B5); падежи после «из N» живут в самих
 * ICU-строках.
 */

import { t } from '../i18n';
import { dateProse } from '@shared/src/format';
import type { components } from '../api/schema';

type DailySnapshot = components['schemas']['DailySnapshot'];

/**
 * Строка основания одной точки: дата, сколько движков ответило, насколько
 * заполнена сетка.
 *
 * `codes` — подключённые движки из справочника, а не только ответившие: движок,
 * который не ответил, обязан быть виден в знаменателе, иначе средняя по экрану
 * молча улучшается ровно тогда, когда источник сломался.
 */
export function snapshotBasis({
  date,
  rows,
  codes,
  gridComplete,
}: {
  date: string;
  rows: DailySnapshot[];
  codes: string[];
  gridComplete?: boolean;
}): string {
  const answered = rows.filter((row) => (row.organic_answers ?? 0) > 0).length;
  const filled = rows.reduce((sum, row) => sum + (row.cells_filled ?? 0), 0);
  const planned = rows.reduce((sum, row) => sum + (row.cells_planned ?? 0), 0);
  const grid =
    planned > 0 ? t('basis.grid', { filled, planned }) : t('basis.grid-unknown');
  return [
    t('basis.data-for', { date: dateProse(date) }),
    t('basis.engines-answered', { answered, total: codes.length }),
    grid,
    ...(gridComplete === false ? [t('basis.grid-incomplete')] : []),
  ].join(' · ');
}

/**
 * Основание матрицы «вопрос × движок»: каким замером она построена и сколько
 * вопросов из него показано.
 *
 * Отдельно от экранного основания и по делу: матрица стоит на ОДНОМ замере, а
 * не на дневной точке, и показывает не всё, что измерено, а первую страницу
 * строк. Без этой подписи пятьдесят строк читаются как всё ядро, а число
 * прогонов на клетку — как всегдашняя двойка, хотя у бесплатного аудита прогон
 * один и состояния «назван в одном из двух» там не существует.
 *
 * Про недочитанный замер сказано словами. Молча укоротить таблицу значит
 * повторить ту же ошибку, из-за которой матрица показывала «Не измерено» по
 * измеренным вопросам.
 */
export function matrixBasis({
  run,
  shown,
  measured,
  complete,
}: {
  run: { snapshot_date?: string | null; runs_per_cell: 1 | 2 } | null;
  shown: number;
  measured: number;
  complete: boolean;
}): string {
  if (!run) return t('basis.no-runs');
  return [
    run.snapshot_date
      ? t('basis.run-dated', { date: dateProse(run.snapshot_date) })
      : t('basis.run-latest'),
    t('basis.per-cell', { n: run.runs_per_cell }),
    shown < measured
      ? t('basis.shown-of', { shown, measured })
      : t('basis.count', { measured }),
    ...(complete ? [] : [t('basis.read-incomplete')]),
  ].join(' · ');
}
